import React, { useState } from 'react';
import { Form, Input, Button, Card, message, Typography, Skeleton } from 'antd';
import { PhoneOutlined, MailOutlined, EnvironmentOutlined, UserOutlined, SendOutlined } from '@ant-design/icons';
import { useContactContent, getContentByKey } from '../../hooks/useDynamicContent';
import api from '../../services/api';

function Contact() {
    const { content: contactContent, loading: contentLoading } = useContactContent();
    const [submitting, setSubmitting] = useState(false);

    const onFinish = async (values) => {
        try {
            setSubmitting(true);
            await api.sendContactMessage(values);
            message.success('Message sent successfully');
        } catch (e) {
            message.error(e.message || 'Failed to send message');
        } finally {
            setSubmitting(false);
        }
    };

    const contactPhone = contentLoading ? '' : getContentByKey(contactContent, 'contact_phone', '+91 7483423925');
    const contactEmail = contentLoading ? '' : getContentByKey(contactContent, 'contact_email', 'connect.spoonandspice@gmail.com');
    const contactAddress = contentLoading ? '' : getContentByKey(contactContent, 'contact_address', '#2, Gokulappa Building, 20th \'L\' Cross Rama Temple Road, Post, Viveknagar, Ejipura, Bengaluru, Karnataka 560047');
    const businessHours = contentLoading ? {} : {
        weekdays: getContentByKey(contactContent, 'business_hours_weekdays', '9:00 AM - 8:00 PM'),
        saturday: getContentByKey(contactContent, 'business_hours_saturday', '10:00 AM – 6:00 PM'),
        sunday: getContentByKey(contactContent, 'business_hours_sunday', 'Closed')
    };

    return (
        <div className="min-h-screen bg-[#FAF8F5] py-12 md:py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header Section */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl md:text-5xl font-bold text-[#2C2C2C] mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
                        {contentLoading ? (<Skeleton.Input active size="large" style={{ width: '200px', margin: '0 auto' }} />) : 'Contact Us'}
                    </h1>
                    <p className="text-base md:text-lg text-[#4A4A4A] max-w-2xl mx-auto">
                        {contentLoading ? (<Skeleton.Input active size="default" style={{ width: '100%', margin: '0 auto' }} />) : getContentByKey(contactContent, 'contact_subtitle', 'Get in touch with us for any questions or to book your catering service.')}
                    </p>
                </div>

                {/* Two Panel Layout */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
                    {/* Left Panel: Contact Details */}
                    <div className="bg-white rounded-2xl shadow-lg p-8">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="w-10 h-10 bg-[#C97D60]/10 rounded-full flex items-center justify-center">
                                <svg className="w-6 h-6 text-[#C97D60]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                            </div>
                            <h2 className="text-2xl font-bold text-[#2C2C2C]">Contact Details</h2>
                        </div>

                        <div className="space-y-6">
                            {/* Phone */}
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 bg-[#C97D60]/10 rounded-full flex items-center justify-center flex-shrink-0">
                                    <PhoneOutlined className="text-[#C97D60] text-lg" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-[#2C2C2C] mb-1">Phone</p>
                                    <p className="text-[#4A4A4A]">
                                        {contentLoading ? (<Skeleton.Input active size="small" style={{ width: '150px' }} />) : contactPhone}
                                    </p>
                                </div>
                            </div>

                            {/* Email */}
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 bg-[#C97D60]/10 rounded-full flex items-center justify-center flex-shrink-0">
                                    <MailOutlined className="text-[#C97D60] text-lg" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-[#2C2C2C] mb-1">Email</p>
                                    <p className="text-[#4A4A4A]">
                                        {contentLoading ? (<Skeleton.Input active size="small" style={{ width: '200px' }} />) : contactEmail}
                                    </p>
                                </div>
                            </div>

                            {/* Address */}
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 bg-[#C97D60]/10 rounded-full flex items-center justify-center flex-shrink-0">
                                    <EnvironmentOutlined className="text-[#C97D60] text-lg" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-[#2C2C2C] mb-1">Address</p>
                                    <p className="text-[#4A4A4A]">
                                        {contentLoading ? (<Skeleton.Input active size="small" style={{ width: '250px' }} />) : contactAddress}
                                    </p>
                                </div>
                            </div>

                            {/* Business Hours */}
                            <div className="pt-6 border-t border-[#EAE7E3]">
                                <h3 className="text-lg font-semibold text-[#2C2C2C] mb-4">Business Hours</h3>
                                <div className="space-y-3">
                                    <div className="flex justify-between items-center">
                                        <span className="text-[#4A4A4A]">Monday - Friday</span>
                                        <span className="text-[#2C2C2C] font-medium">{businessHours.weekdays}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-[#4A4A4A]">Saturday</span>
                                        <span className="text-[#2C2C2C] font-medium">{businessHours.saturday}</span>
                                    </div>
                                    <div className="flex justify-between items-center">
                                        <span className="text-[#4A4A4A]">Sunday</span>
                                        <span className="text-[#2C2C2C] font-medium">{businessHours.sunday}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Panel: Send us a message */}
                    <div className="bg-white rounded-2xl shadow-lg p-8">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="w-10 h-10 bg-[#C97D60]/10 rounded-full flex items-center justify-center">
                                <SendOutlined className="w-6 h-6 text-[#C97D60]" />
                            </div>
                            <h2 className="text-2xl font-bold text-[#2C2C2C]">Send us a message</h2>
                        </div>

                        <Form layout="vertical" onFinish={onFinish} className="space-y-6">
                            <Form.Item 
                                label={<span className="text-[#2C2C2C] font-medium">* Your Name</span>} 
                                name="name" 
                                rules={[{ required: true, message: 'Please enter your name' }]}
                            >
                                <Input 
                                    placeholder="Full Name" 
                                    className="h-12 rounded-lg border-gray-300 focus:border-[#C97D60] focus:ring-[#C97D60]"
                                    suffix={<UserOutlined className="text-[#C97D60]" />}
                                />
                            </Form.Item>

                            <Form.Item 
                                label={<span className="text-[#2C2C2C] font-medium">* Email</span>} 
                                name="email" 
                                rules={[{ required: true, message: 'Please enter your email' }, { type: 'email', message: 'Enter a valid email' }]}
                            >
                                <Input 
                                    placeholder="you@example.com" 
                                    className="h-12 rounded-lg border-gray-300 focus:border-[#C97D60] focus:ring-[#C97D60]"
                                    suffix={
                                        <svg className="w-5 h-5 text-[#C97D60]" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M8.1 13.34l2.83-2.83L3.91 3.5c-1.56 1.56-1.56 4.09 0 5.66l4.19 4.18zm6.78-1.81c1.53.71 3.68.21 5.27-1.38 1.91-1.91 2.28-4.65.81-6.12-1.46-1.46-4.2-1.1-6.12.81-1.59 1.59-2.09 3.74-1.38 5.27L3.7 19.87l1.41 1.41L12 14.41l6.88 6.88 1.41-1.41L13.41 13l1.47-1.47z"/>
                                        </svg>
                                    }
                                />
                            </Form.Item>

                            <Form.Item 
                                label={<span className="text-[#2C2C2C] font-medium">Subject</span>} 
                                name="subject"
                            >
                                <Input 
                                    placeholder="What is this about?" 
                                    className="h-12 rounded-lg border-gray-300 focus:border-[#C97D60] focus:ring-[#C97D60]"
                                    suffix={
                                        <svg className="w-5 h-5 text-[#C97D60]" fill="currentColor" viewBox="0 0 24 24">
                                            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"/>
                                            <path d="M8.5 8.5c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5S7 10.83 7 10s.67-1.5 1.5-1.5zm7 0c.83 0 1.5.67 1.5 1.5s-.67 1.5-1.5 1.5-1.5-.67-1.5-1.5.67-1.5 1.5-1.5zM12 17.5c2.33 0 4.31-1.46 5.11-3.5H6.89c.8 2.04 2.78 3.5 5.11 3.5z"/>
                                        </svg>
                                    }
                                />
                            </Form.Item>

                            <Form.Item 
                                label={<span className="text-[#2C2C2C] font-medium">* Message</span>} 
                                name="message" 
                                rules={[{ required: true, message: 'Please enter a message' }]}
                            >
                                <div className="relative">
                                    <Input.TextArea 
                                        rows={4} 
                                        placeholder="How can we help you?" 
                                        className="rounded-lg border-gray-300 focus:border-[#C97D60] focus:ring-[#C97D60] pr-10"
                                    />
                                    <div className="absolute bottom-3 right-3">
                                        <svg className="w-5 h-5 text-[#C97D60]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                        </svg>
                                    </div>
                                </div>
                            </Form.Item>

                            <Form.Item>
                                <Button 
                                    type="primary" 
                                    htmlType="submit" 
                                    loading={submitting}
                                    size="large"
                                    className="w-full h-12 rounded-lg bg-[#C97D60] hover:bg-[#B8735A] border-none font-semibold"
                                    icon={<SendOutlined />}
                                >
                                    Send Message
                                </Button>
                            </Form.Item>
                        </Form>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Contact;
